import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import type { Profile } from '@/types/database'
import { identify, resetAnalytics } from '@/lib/analytics'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  profileLoading: boolean
  initialized: boolean   // true once INITIAL_SESSION has fired — public routes can render
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

// ─── Profile cache (localStorage) ────────────────────────────────────────────
//
// Stores the profile JSON so the next page load / refresh is instant.
// The cache is validated against the current userId in onAuthStateChange.

const CACHE_KEY = 'meudriver:profile'

function readProfileCache(): Profile | null {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as Profile | null }
  catch { return null }
}
function writeProfileCache(p: Profile | null) {
  try {
    if (p) localStorage.setItem(CACHE_KEY, JSON.stringify(p))
    else localStorage.removeItem(CACHE_KEY)
  } catch { /* ignore quota errors */ }
}

// ─── Sentinel for a slow-network timeout (not a DB error) ────────────────────

class ProfileFetchTimeout extends Error {
  constructor() { super('Profile fetch timed out') }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// ─── Provider ─────────────────────────────────────────────────────────────────

function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false)
  const [user, setUser]       = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // ── profileRef — lets the auth handler read current profile without stale closure
  const profileRef = useRef<Profile | null>(null)
  useEffect(() => { profileRef.current = profile }, [profile])

  // ── fetchProfile ──────────────────────────────────────────────────────────
  //
  // Returns 'success' | 'error' | 'timeout'.
  // On success, also writes the fresh profile to the localStorage cache.

  const fetchProfile = useCallback(async (userId: string): Promise<'success' | 'error' | 'timeout'> => {
    function raceTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
      return Promise.race([
        promise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new ProfileFetchTimeout()), ms),
        ),
      ])
    }

    try {
      const { data, error } = await raceTimeout(
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single() as unknown as Promise<{ data: Profile | null; error: import('@supabase/supabase-js').PostgrestError | null }>,
        10_000,
      )

      if (error) {
        // PGRST116 = no rows → profile never created, auto-create from metadata
        if (error.code === 'PGRST116') {
          const { data: { user: authUser }, error: getUserError } = await raceTimeout(
            supabase.auth.getUser(),
            10_000,
          )
          if (getUserError || !authUser) { setProfile(null); writeProfileCache(null); return 'error' }

          const meta = authUser.user_metadata ?? {}
          const role = (['student', 'instructor', 'admin'] as const).includes(meta.role)
            ? meta.role as Profile['role']
            : 'student' as Profile['role']

          const { data: newProfile, error: upsertError } = await raceTimeout(
            supabase
              .from('profiles')
              .upsert(
                { id: userId, full_name: meta.full_name ?? authUser.email ?? '', phone: meta.phone ?? null, role },
                { onConflict: 'id' },
              )
              .select()
              .single() as unknown as Promise<{ data: Profile | null; error: import('@supabase/supabase-js').PostgrestError | null }>,
            10_000,
          )

          if (upsertError) { setProfile(null); writeProfileCache(null); return 'error' }
          setProfile(newProfile as Profile)
          writeProfileCache(newProfile as Profile)
          return 'success'
        }

        setProfile(null)
        writeProfileCache(null)
        return 'error'
      }

      setProfile(data as Profile)
      writeProfileCache(data as Profile)
      return 'success'

    } catch (err) {
      if (err instanceof ProfileFetchTimeout) {
        if (import.meta.env.DEV) {
          console.warn('[AuthContext] fetchProfile timed out — Supabase may be waking from pause.')
        }
        return 'timeout'
      }
      Sentry.captureException(err, { tags: { source: 'AuthContext.fetchProfile' } })
      if (import.meta.env.DEV) {
        console.error('[AuthContext] fetchProfile unexpected error:', err)
      }
      setProfile(null)
      return 'error'
    }
  }, [])

  // ── refreshProfile ────────────────────────────────────────────────────────

  const refreshProfile = useCallback(async () => {
    if (!user) return
    await fetchProfile(user.id)
  }, [user, fetchProfile])

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  //
  // Strategy:
  //   INITIAL_SESSION — check localStorage cache first.
  //     Cache hit  → render immediately, re-fetch in background (silent update).
  //     Cache miss → show spinner, fetch profile, then render.
  //   TOKEN_REFRESHED — don't block UI at all.
  //     Profile exists → no-op (user is back from background tab).
  //     Profile null   → silent retry (wake from free-tier pause).
  //   SIGNED_IN — normal fetch with spinner (first login).
  //
  // Safety valve: 45 s timer gives up and unblocks the UI.

  const activeHandlerRef = useRef(true)

  useEffect(() => {
    activeHandlerRef.current = true

    const safetyTimer = setTimeout(() => {
      Sentry.captureMessage('AuthContext safety timer fired after 45s', 'warning')
      if (import.meta.env.DEV) {
        console.warn('[AuthContext] safety timer fired after 45 s')
      }
      setLoading(false)
      setProfileLoading(false)
      setInitialized(true)
    }, 45_000)

    const handleAuthChange = async (_event: AuthChangeEvent, session: Session | null) => {
      const currentUser = session?.user ?? null

      // ── STATE UPDATES BEFORE FIRST AWAIT ──────────────────────────────
      // React batches all synchronous setState calls that happen before the
      // first await. This is the key to avoiding flashes: initialized, user,
      // and (on cache hit) loading all flip in a single React commit.

      setUser(currentUser)
      setInitialized(true)     // public routes can now render

      if (!currentUser) {
        setProfile(null)
        writeProfileCache(null)
        resetAnalytics()        // Posthog: limpa sessão anônima
        clearTimeout(safetyTimer)
        setLoading(false)
        setProfileLoading(false)
        return
      }

      // Posthog: vincula a sessão ao user_id (sem PII como propriedade)
      identify(currentUser.id)

      // ── TOKEN_REFRESHED — never block the UI ──────────────────────────
      if (_event === 'TOKEN_REFRESHED') {
        if (profileRef.current !== null) return   // already have profile — nothing to do
        // Profile is null: Supabase just woke from pause. Silent retry.
        setProfileLoading(true)
        const result = await fetchProfile(currentUser.id)
        if (result !== 'timeout' && activeHandlerRef.current) {
          clearTimeout(safetyTimer)
          setLoading(false)
          setProfileLoading(false)
        }
        return
      }

      // ── INITIAL_SESSION: try the cache first ──────────────────────────
      if (_event === 'INITIAL_SESSION') {
        const cached = readProfileCache()
        if (cached?.id === currentUser.id) {
          // Cache hit — render immediately
          setProfile(cached)
          setLoading(false)
          setProfileLoading(false)
          clearTimeout(safetyTimer)
          // Background refresh so stale data gets corrected silently
          fetchProfile(currentUser.id)
          return
        }
      }

      // ── INITIAL_SESSION (no cache) or SIGNED_IN — full fetch ──────────
      setLoading(true)
      setProfileLoading(true)

      const result = await fetchProfile(currentUser.id)

      if (result === 'timeout') {
        // Keep spinner up; TOKEN_REFRESHED will retry after Supabase wakes.
        return
      }

      if (activeHandlerRef.current) {
        clearTimeout(safetyTimer)
        setLoading(false)
        setProfileLoading(false)
      }
    }

    // O callback do onAuthStateChange roda segurando a trava interna do
    // supabase-js; qualquer chamada ao Supabase feita dentro dele (o select do
    // profile) espera essa mesma trava e só segue no timeout — a tela de
    // "Carregando" ficava ~10 s parada ao reabrir o site logado. Por isso o
    // trabalho é adiado para fora do callback (recomendação do Supabase).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => { void handleAuthChange(event, session) }, 0)
    })

    return () => {
      activeHandlerRef.current = false
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
  }, [fetchProfile])

  // ── Sign-out ──────────────────────────────────────────────────────────────

  const signOut = useCallback(async () => {
    setUser(null)
    setProfile(null)
    writeProfileCache(null)
    supabase.auth.signOut().catch((err) => {
      Sentry.captureException(err, { tags: { source: 'AuthContext.signOut' } })
      if (import.meta.env.DEV) {
        console.error('[AuthContext] signOut network error:', err)
      }
    })
  }, [])

  const value: AuthContextType = {
    user, profile, loading, profileLoading, initialized, signOut, refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export { AuthContext, AuthProvider, useAuth }
export type { AuthContextType }
